import { useFormik } from 'formik';

import {
  CheckboxOption,
  emptyAnswerFor,
  isKnownQuestionType,
  QuestionTypeEnum,
  SurveyPageResponse,
  SurveyPageType,
  FormResponse,
  SurveyQuestion,
} from '@employ-engage/data';
import { useMutation } from '@apollo/client';

import { Fragment, useEffect, useState } from 'react';
import {
  EECheckboxGroup,
  EERadioGroup,
  EETextArea,
  ErrorMessage,
  LoadingSpinner,
  Modal,
  useSurveyResponseUtils,
  UPDATE_FORM_RESPONSE_NO_AUTH,
  DELETE_FORM_RESPONSE_NO_AUTH,
  deepOmit,
} from '@employ-engage/common-components';
import { buildValidationSchema } from './survey-validation';
import isEmpty from 'lodash.isempty';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface SurveyPageFormProps {
  completionButtonLabel?: string;
  templatePages: SurveyPageType[];
  currentPage: SurveyPageType;
  currentPageResponse: SurveyPageResponse | null;
  setCurrentPageResponse: (response: SurveyPageResponse | null) => void;
  currentResponse: FormResponse;
  setCurrentResponse: (response: FormResponse | null) => void;
  isLastPage: boolean;
  surveyId: string;
  employeeId: string;
  responseId: string;
  pageNumber: string;
}

/** Every point on a question's scale, defaulting to 1-5 when unset. */
const scalePoints = (question: SurveyQuestion) => {
  const min = question.scale?.min ?? 1;
  const max = question.scale?.max ?? 5;
  if (max < min) return [];
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
};

export function SurveyPageForm({
  completionButtonLabel,
  templatePages,
  currentPage,
  currentPageResponse,
  setCurrentPageResponse,
  currentResponse,
  setCurrentResponse,
  isLastPage,
  surveyId,
  employeeId,
  responseId,
  pageNumber,
}: SurveyPageFormProps) {
  const { t } = useTranslation(['common']);
  const location = useLocation();
  const navigate = useNavigate();
  const [isTransitioningBetweenPages, setIsTransitioningBetweenPages] =
    useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { calculateQuestionNumber } = useSurveyResponseUtils();
  const [deleteFormResponseNoAuth] = useMutation(DELETE_FORM_RESPONSE_NO_AUTH);

  const friendlyCompanyId = location.pathname.split('/')[1];

  const initialValues: SurveyPageResponse = currentPageResponse
    ? currentPageResponse
    : {
        answers: currentPage.questions.map(
          (question) =>
            emptyAnswerFor(question.type) as SurveyPageResponse['answers'][number]
        ),
      };

  useEffect(() => {
    setIsTransitioningBetweenPages(false);
  }, [pageNumber]);

  const sendResponseEmailsIfNeeded = async (
    formResponseId: string,
    formId: string
  ) => {
    await fetch(
      `${
        import.meta.env.VITE_API_ENDPOINT
      }/api/enqueue-emails?type=send-responses`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formResponseId,
          formId,
        }),
      }
    );
  };

  const { handleSubmit, values, errors, handleChange, touched, setFieldValue } =
    useFormik<SurveyPageResponse>({
      initialValues,
      onSubmit: async (values, { resetForm, validateForm, setErrors }) => {
        const errors = validateForm(values);
        if (!isEmpty(errors)) {
          return;
        }
        if (isLastPage) {
          await updateResponse(values, true);

          // TODO: Send Email if enabled
          await sendResponseEmailsIfNeeded(responseId, surveyId);
          resetForm();
          setCurrentResponse(null);
          setCurrentPageResponse(null);
          navigate(
            `/${friendlyCompanyId}/form/${surveyId}/employee/${employeeId}/complete`
          );
        } else {
          setIsTransitioningBetweenPages(true);
          const newPages = await updateResponse(values);
          resetForm();
          setCurrentResponse(null);
          setCurrentPageResponse(null);
          navigate(
            `/${friendlyCompanyId}/form/${surveyId}/employee/${employeeId}/response/${responseId}/page/${
              parseInt(pageNumber || '0', 10) + 1
            }`,
            {
              state: {
                prevPages: newPages,
              },
            }
          );
        }
      },
      validationSchema: buildValidationSchema(currentPage.questions, t),
    });

  const [updateFormResponseNoAuth] = useMutation(UPDATE_FORM_RESPONSE_NO_AUTH);

  // A declaration so useFormik's onSubmit, written above it, can call it;
  // it in turn needs the mutation hook declared above here.
  async function updateResponse(
    values: any,
    isComplete = false
  ): Promise<any> {
    const newPages = [
      ...currentResponse.pages.slice(0, parseInt(pageNumber, 10)),
      values,
      ...currentResponse.pages.slice(parseInt(pageNumber, 10) + 1),
    ];

    // Prepare the input for the mutation
    const input = {
      employeeId,
      companyId: currentResponse.companyId,
      surveyId: currentResponse.surveyId,
      pages: newPages,
      employeeDepartmentWhenCreated:
        currentResponse.employeeDepartmentWhenCreated,
    };

    const cleanPages = deepOmit(newPages, ['__typename']);

    await updateFormResponseNoAuth({
      variables: {
        id: responseId,
        input: {
          ...input,
          pages: cleanPages,
          dateCompleted: isComplete ? new Date() : null,
        },
      },
    });
    return cleanPages;
  }

  const handleAnswerSelect = (val: any, questionIndex: number) => {
    setFieldValue(`answers[${questionIndex}]`, val);
  };

  const handleCheckboxAnswerSelect = (
    e: any,
    questionIndex: number,
    option: CheckboxOption,
    value: { subAnswers: never[] }
  ) => {
    if (e.target.checked) {
      const newSubAnswers = [...value.subAnswers, option];
      setFieldValue(
        `answers[${questionIndex}].subAnswers`,
        newSubAnswers,
        true
      );
    } else {
      const newSubAnswers = value.subAnswers.filter(
        (v: { id: string }) => v.id !== option.id
      );
      setFieldValue(
        `answers[${questionIndex}].subAnswers`,
        newSubAnswers,
        true
      );
    }
  };

  const pageIndex = templatePages.findIndex((page) => page === currentPage);

  if (isTransitioningBetweenPages)
    return <LoadingSpinner spinnerId="survey-page-form-loading" />;

  return (
    <form onSubmit={handleSubmit}>
      {currentPage.questions.map((question, questionIndex) => (
        <Fragment key={questionIndex}>
          <h2 className="text-lg font-bold">
            {calculateQuestionNumber(pageIndex, questionIndex, templatePages)}.{' '}
            {question.question}
            {question.isRequired && (
              <span className="text-semantic-error">*</span>
            )}
          </h2>
          <div className="mb-10">
            {question.type === QuestionTypeEnum.SELECT_ONE && (
              <EERadioGroup
                isColumn={question?.isColumn}
                value={values.answers[questionIndex]}
                onChange={(value: any) =>
                  handleAnswerSelect(value, questionIndex)
                }
                options={question?.options}
                errors={errors.answers?.[questionIndex]}
                touched={touched.answers || false}
              />
            )}
            {question.type === QuestionTypeEnum.SELECT_MANY &&
              !!question?.options && (
                <EECheckboxGroup
                  value={values.answers[questionIndex]}
                  onChange={(value: any, option: CheckboxOption) =>
                    handleCheckboxAnswerSelect(
                      value,
                      questionIndex,
                      option,
                      values?.answers[questionIndex] as { subAnswers: never[] }
                    )
                  }
                  options={question?.options}
                  errors={
                    (
                      errors.answers?.[questionIndex] as unknown as {
                        subAnswers: string;
                      }
                    )?.subAnswers
                  }
                  touched={touched.answers || false}
                />
              )}
            {question.type === QuestionTypeEnum.LONG_TEXT && (
              <EETextArea
                name={`answers[${questionIndex}]`}
                value={values.answers[questionIndex] as string}
                placeholder={t('Type your answer here...')}
                onChange={handleChange}
                errors={errors?.answers?.[questionIndex]}
                touched={touched.answers || false}
              />
            )}
            {question.type === QuestionTypeEnum.SHORT_TEXT && (
              <div>
                <input
                  type="text"
                  className="rounded-md border-2 border-gray-400 p-2 text-custom-textColor w-full"
                  name={`answers[${questionIndex}]`}
                  value={values.answers[questionIndex] as string}
                  placeholder={t('Type your answer here...')}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <ErrorMessage
                  errors={errors?.answers?.[questionIndex]}
                  touched={touched.answers || false}
                />
              </div>
            )}
            {question.type === QuestionTypeEnum.SCALE && (
              <div>
                {/* Radio buttons rather than a slider: a slider always shows a
                    value, so it cannot tell "chose the middle" from "did not
                    answer", and it starts every respondent at a number they
                    did not pick. */}
                <div className="flex flex-wrap gap-2">
                  {scalePoints(question).map((point) => (
                    <label
                      key={point}
                      className={classNames(
                        'cursor-pointer rounded border-2 px-4 py-2',
                        values.answers[questionIndex] === point
                          ? 'border-light-primary bg-light-primary text-white'
                          : 'border-gray-400'
                      )}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name={`answers[${questionIndex}]`}
                        checked={values.answers[questionIndex] === point}
                        onChange={() =>
                          setFieldValue(`answers[${questionIndex}]`, point)
                        }
                      />
                      {point}
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  errors={errors?.answers?.[questionIndex]}
                  touched={touched.answers || false}
                />
              </div>
            )}
            {question.type === QuestionTypeEnum.NUMBER && (
              <div>
                <input
                  type="number"
                  className="rounded-md border-2 border-gray-400 p-2 text-custom-textColor"
                  name={`answers[${questionIndex}]`}
                  value={
                    values.answers[questionIndex] === null ||
                    values.answers[questionIndex] === undefined
                      ? ''
                      : (values.answers[questionIndex] as number)
                  }
                  onChange={(e) =>
                    // Empty stays null, never 0: a blank exported as zero
                    // reads as a recorded value rather than a missing one.
                    setFieldValue(
                      `answers[${questionIndex}]`,
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                />
                <ErrorMessage
                  errors={errors?.answers?.[questionIndex]}
                  touched={touched.answers || false}
                />
              </div>
            )}
            {question.type === QuestionTypeEnum.DATE && (
              <div>
                <input
                  type="date"
                  className="rounded-md border-2 border-gray-400 p-2 text-custom-textColor"
                  name={`answers[${questionIndex}]`}
                  value={values.answers[questionIndex] as string}
                  onChange={handleChange}
                />
                <ErrorMessage
                  errors={errors?.answers?.[questionIndex]}
                  touched={touched.answers || false}
                />
              </div>
            )}
            {/* Without this the question renders as nothing at all, and the
                respondent sees a numbered heading with empty space under it
                and no way to know whether they have missed something. Paired
                with the validation schema, which never requires a type it
                cannot render. */}
            {!isKnownQuestionType(question.type) && (
              <p className="rounded bg-semantic-warning/20 p-3 text-sm">
                {t(
                  'This question cannot be shown in this version of the app, ' +
                    'so it has been skipped. You can still submit your answers.'
                )}
              </p>
            )}
          </div>
        </Fragment>
      ))}
      <div className="flex justify-center">
        <button
          onClick={() => {
            setIsModalOpen(true);
          }}
          type="button"
          className="cursor-pointer bg-light-primary dark:bg-dark-secondary hover:bg-dark-secondaryAccent text-white dark:text-white font-bold py-2 px-4 rounded mr-5"
        >
          {t('Cancel')}
        </button>
        {parseInt(pageNumber, 10) > 0 && (
          <button
            onClick={() => {
              navigate(
                `/${friendlyCompanyId}/form/${surveyId}/employee/${employeeId}/response/${responseId}/page/${
                  parseInt(pageNumber || '0', 10) - 1
                }
            `,
                {
                  state: {
                    prevPages:
                      location?.state?.prevPages ||
                      currentResponse.pages[
                        parseInt(pageNumber || '0', 10) - 1
                      ],
                  },
                }
              );
              setCurrentResponse(null);
              setCurrentPageResponse(null);
            }}
            type="button"
            className="cursor-pointer bg-light-primary dark:bg-dark-primary hover:bg-dark-secondaryAccent text-white dark:text-custom-gray600 font-bold py-2 px-4 rounded mr-5"
          >
            {t('Back')}
          </button>
        )}
        <button
          type="submit"
          className={classNames(
            'cursor-pointer bg-light-primary dark:bg-dark-primary hover:bg-dark-secondaryAccent text-white dark:text-custom-gray600 font-bold py-2 px-4 rounded',
            {
              'opacity-50 cursor-not-allowed': !isEmpty(errors),
            }
          )}
          disabled={!isEmpty(errors)}
        >
          {isLastPage
            ? completionButtonLabel
              ? completionButtonLabel
              : t('Complete Form')
            : t('Save & Continue')}
        </button>
      </div>
      <Modal open={isModalOpen} setOpen={setIsModalOpen}>
        <div className="mb-10">
          {t('Are you sure you want to cancel? All progress will be lost.')}
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setIsModalOpen(false)}
            className="mr-2 bg-light-primary dark:bg-dark-primary hover:bg-dark-primaryBackground text-white dark:text-custom-gray600 font-bold py-2 px-4 rounded"
          >
            {t('No, I changed my mind')}
          </button>
          <button
            onClick={async () => {
              await deleteFormResponseNoAuth({
                variables: { id: responseId },
              });
              navigate(`/${friendlyCompanyId}/form`);
            }}
            className="bg-red-600 hover:bg-red-700 text-white dark:text-custom-gray600 font-bold py-2 px-4 rounded"
          >
            {t('Yes, Cancel')}
          </button>
        </div>
      </Modal>
    </form>
  );
}

export default SurveyPageForm;
